import React, { useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { queryCache, QueryKey, useMutation } from "react-query";
import { AxiosError } from "axios";
import classNames from "classnames";

import {
  Button,
  Dialog,
  TextInput,
  Loader,
  DraftFileUploader,
  ApiErrorBanner,
  MultiSelectInput,
  showNotification,
  Checkbox,
} from "fe-shared/src/components";
import { DialogHeader } from "fe-shared-transactions/src/components";

import useValidation from "fe-shared/src/hooks/useValidation";
import useInvoiceAdjustmentReasons from "hooks/useInvoiceAdjustmentReasons";
import { priceToFormat } from "fe-shared-transactions/src/helpers/invoice";
import { transformCurrencyValue } from "fe-shared/src/helpers/transform";

import { ApiError } from "fe-shared/src/types/common";
import { Invoice } from "fe-shared-transactions/src/types";

import { COMMENT_LENGTH_LIMIT } from "consts";

import {
  initialContent,
  errorMessages,
  getValidationConfig,
  InvoiceAdjustmentContent,
} from "./modules/validation";

import * as api from "./modules/api";

import styles from "./InvoiceAdjustmentDialog.module.scss";

interface Props {
  invoice: Invoice;
  isActive: boolean;
  onClose: () => void;
  isLoading?: boolean;
  activitiesQueryKey: QueryKey;
  invoiceDetailsQueryKey: QueryKey;
}

const InvoiceAdjustmentDialog = (props: Props) => {
  const {
    invoice,
    isActive,
    onClose,
    activitiesQueryKey,
    invoiceDetailsQueryKey,
  } = props;
  const originalSum = invoice.total;
  const { t } = useTranslation();
  const {
    data: reasonOptions,
    isLoading: isLoadingReasons,
  } = useInvoiceAdjustmentReasons();
  const blurTimeoutRef = useRef<NodeJS.Timeout>();

  const validationConfig = useMemo(() => getValidationConfig(originalSum), [
    originalSum,
  ]);

  const {
    errors,
    values,
    isValid,
    validateAll,
    updateValue,
    validateValue,
  } = useValidation(validationConfig, initialContent, errorMessages);
  const { comment, reasonCodes } = values;
  const newTotal = invoice.total + Number(values.amount);
  const isCommentTooLong = comment.length > COMMENT_LENGTH_LIMIT;
  const characterLimitText = `${comment.length.toLocaleString()}/${COMMENT_LENGTH_LIMIT.toLocaleString()}`;
  const reasonsPlaceholder = useMemo(
    () =>
      reasonCodes.length
        ? reasonCodes.map((id) => reasonOptions?.labelsByValue[id]).join(", ")
        : t("invoices.adjustment.form.reasonCodesPlaceholder"),
    [t, reasonCodes, reasonOptions]
  );

  const [adjustInvoice, adjustInvoiceResult] = useMutation<
    unknown,
    AxiosError<ApiError>,
    InvoiceAdjustmentContent
  >(
    (data) =>
      api.adjustInvoice({
        data,
        invoiceId: invoice.invoiceId,
      }),
    {
      throwOnError: true,
      onSuccess: () => {
        onClose();
        showNotification(t("invoices.adjustment.successToast"));
        queryCache.invalidateQueries(activitiesQueryKey);
        queryCache.invalidateQueries(invoiceDetailsQueryKey);
      },
    }
  );

  useEffect(() => () => clearTimeout(blurTimeoutRef.current!), []);

  const onSubmit = () => {
    if (isValid) {
      adjustInvoice(values);
    } else {
      validateAll();
    }
  };

  const isSaveDisabled = !!Object.keys(errors).length || isCommentTooLong;

  const actions = [
    <Button
      key="1"
      variant="filled"
      onClick={onSubmit}
      disabled={isSaveDisabled}
      id="invoice-adjustment-dialog-save-btn"
    >
      {t(`invoices.adjustment.create`)}
    </Button>,
    <Button
      key="2"
      onClick={onClose}
      variant="outlined"
      id="invoice-adjustment-dialog-cancel-btn"
    >
      {t("cancel")}
    </Button>,
  ];

  const title = (
    <DialogHeader
      type="Invoice"
      translationRoot="invoices"
      number={invoice.invoiceNumber}
      title={t("invoices.adjustment.title")}
    />
  );

  const isLoading =
    props.isLoading || isLoadingReasons || adjustInvoiceResult.isLoading;

  return (
    <Dialog
      modal
      width={584}
      title={title}
      isActive={isActive}
      onClose={onClose}
      actions={actions}
    >
      {isLoading && <Loader isBlocking />}
      {adjustInvoiceResult.isError && (
        <ApiErrorBanner
          id="adjust-invoice-error-banner"
          requestStatus={adjustInvoiceResult}
        />
      )}
      <h2 className={styles.formSectionTitle}>
        {t("invoices.adjustment.form.detailsTitle")}
      </h2>
      <div className={styles.adjustedSumInput}>
        <TextInput
          id="amount"
          type="number"
          width={160}
          value={values.amount}
          isError={!!errors.amount}
          errorText={errors.amount}
          transformValue={transformCurrencyValue}
          onBlur={() => validateValue("amount")}
          messageClassName={styles.inputMessage}
          title={t("invoices.adjustment.form.sumTitle")}
          onChange={(e, value) => updateValue("amount", value)}
        />
        <span className={styles.adjustedSumOriginal}>
          {t("invoices.adjustment.newTotal")}
          <span className={classNames(newTotal < 0 && styles.negative)}>
            {priceToFormat(newTotal)}
          </span>
        </span>
      </div>
      <MultiSelectInput
        id="reasonCodes"
        value={reasonCodes}
        options={reasonOptions?.items}
        isError={!!errors.reasonCodes}
        errorText={errors.reasonCodes}
        placeholder={reasonsPlaceholder}
        wrapperClassName={styles.formInput}
        title={t("invoices.adjustment.form.reasonCodesTitle")}
        onBlur={() => {
          blurTimeoutRef.current = setTimeout(
            () => validateValue("reasonCodes"),
            200
          );
        }}
        onChange={(e, v) => updateValue("reasonCodes", v, true)}
      />
      <TextInput
        isMultiline
        id="comment"
        height={80}
        value={values.comment}
        isError={!!errors.comment}
        title={t("invoices.adjustment.form.comment")}
        placeholder={t("invoices.adjustment.form.commentPlaceholder")}
        errorText={
          isCommentTooLong
            ? t("validation.commentMaxLength", { characterLimitText })
            : errors.comment
        }
        onBlur={() => validateValue("comment")}
        onChange={(e, value) => updateValue("comment", value, true)}
        wrapperClassName={styles.formInput}
      />
      <Checkbox
        id="isCommentVisibleToParticipant"
        value={values.isCommentVisibleToParticipant}
        title={t("invoices.adjustment.form.showToParticipantTitle")}
        onChange={(e, value) => {
          updateValue("isCommentVisibleToParticipant", value);
        }}
      />
      <h2 className={styles.formSectionTitle}>
        {t("invoices.adjustment.form.documentsTitle")}
      </h2>
      <DraftFileUploader
        path="invoices/attachment"
        files={values.attachments}
        onChange={(files) => updateValue("attachments", files, true)}
      />
    </Dialog>
  );
};

export default InvoiceAdjustmentDialog;
